import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import api from "../api/client";
import { resumesAPI, profileAPI } from "../api/client";
import { CANDIDATE_STATUSES, getStatusStyle, getStatusLabel } from "../constants/candidateStatuses";
import "../pages/AnimationsAndStyles.css";
import LoadingButton from "../components/LoadingButton";
import { mayPerformWithRole } from "../utils/userAccess";

const parseDate = (str) => {
  if (!str) return null;
  const s = String(str);
  return new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
};

export default function Profile({ onLogout }) {
  const navigate = useNavigate();

  // MSAL setup (unchanged)
  let instance;
  try {
    const msal = useMsal();
    instance = msal?.instance;
  } catch (err) {
    console.warn("MSAL not initialized:", err.message);
    instance = null;
  }

  /* ================= ENHANCED STATE WITH GRAPHICS ================= */
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("profile");
  
  // Resume state
  const [resumeUpdates, setResumeUpdates] = useState([]);
  const [resumeLoading, setResumeLoading] = useState(false);
  
 // Search state
const [resumeSearch, setResumeSearch] = useState("");

  const filteredResumes = resumeUpdates.filter(item => {
  const search = resumeSearch.toLowerCase();

  return (
    item.candidate_name?.toLowerCase().includes(search) ||
    item.updated_by_name?.toLowerCase().includes(search)
  );
});

  // Status activity state
  const [statusLogs, setStatusLogs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusLoading, setStatusLoading] = useState(false);

  // My Performance state
  const [perfMode, setPerfMode] = useState("lastWeek");
  const [perfStartDate, setPerfStartDate] = useState("");
  const [perfEndDate, setPerfEndDate] = useState("");
  const [perfGenerating, setPerfGenerating] = useState(false);
  const [perfError, setPerfError] = useState("");

  /* ================= GRAPHICS INTEGRATION ================= */
  const getStatusIcon = (status) => {
    return "";
  };

  const getStatusColor = (status) => {
    if (status === "all") return '#64748B';
    const findStatus = CANDIDATE_STATUSES.find(s => s.key === status);
    return findStatus?.color || '#64748B';
  };

  /* ================= DOWNLOAD & FETCH FUNCTIONS (UNCHANGED) ================= */
const handleDownload = async (resumeId) => {
  try {
    const res = await resumesAPI.download(resumeId);

    const downloadUrl = res.data.downloadUrl;

    if (!downloadUrl) {
      toast.error("No download URL received");
      return;
    }

    // Open signed S3 URL
    window.open(downloadUrl, "_blank", "noopener,noreferrer");

  } catch (err) {
    console.error(err);
    toast.error("Download failed");
  }
};

  /* ================= MY PERFORMANCE EXCEL (PHASE 3) ================= */
  const handleGenerateExcel = async () => {
    if (perfGenerating) return;
    setPerfGenerating(true);
    setPerfError("");

    try {
      const params = {};
      if (perfMode === "custom") {
        if (!perfStartDate || !perfEndDate) {
          toast.error("Please select both From and To dates");
          setPerfGenerating(false);
          return;
        }
        params.mode = "custom";
        params.startDate = perfStartDate;
        params.endDate = perfEndDate;
      } else {
        params.mode = perfMode;
      }

      const res = await api.get("/performance/my", { params });
      const data = res.data || {};

      if (!data.hasData || !Array.isArray(data.values)) {
        toast.info("No activity found for this period");
        setPerfGenerating(false);
        return;
      }

      const { userName, startDate, endDate } = data;

      const exportRes = await api.get("/performance/my/export", { params, responseType: "blob" });
      const blob = new Blob([exportRes.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = String(userName).replace(/[\\/:*?"<>|]+/g, "_").trim();
      link.download = `My_Performance_${safeName}_${startDate || "start"}_${endDate || "end"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel generated successfully!");
    } catch (err) {
      console.error("My performance excel failed:", err);
      setPerfError("Failed to generate the report. Please try again.");
      toast.error(`Failed to generate report: ${err?.response?.data?.error || err?.message || "Unknown error"}`);
    } finally {
      setPerfGenerating(false);
    }
  };


  // const handleDownload = async (resumeId) => {
  //   try {
  //     const res = await resumesAPI.download(resumeId);
  //     const url = window.URL.createObjectURL(new Blob([res.data]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", `resume-${resumeId}.pdf`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //     window.URL.revokeObjectURL(url);
  //     toast.success("Resume downloaded!");
  //   } catch (err) {
  //     toast.error("Download failed");
  //   }
  // };

  // Fetch resume updates (unchanged)
  useEffect(() => {
    if (activeTab !== "resumes") return;
    const fetchResumeUpdates = async () => {
      try {
        setResumeLoading(true);
        const res = await resumesAPI.getAllUpdates();
        setResumeUpdates(res.data);
      } catch (err) {
        toast.error("Failed to load resume updates");
      } finally {
        setResumeLoading(false);
      }
    };
    fetchResumeUpdates();
  }, [activeTab]);

  // Fetch status activity (enhanced with graphics prep)
  useEffect(() => {
  if (activeTab !== "status") return;

  const fetchStatusLogs = async () => {
    try {
      setStatusLoading(true);
      const res = await profileAPI.getStatusActivity(); // ✅ CORRECT
      setStatusLogs(res.data);
    } catch (err) {
      toast.error("Failed to load status activity");
    } finally {
      setStatusLoading(false);
    }
  };

  fetchStatusLogs();
}, [activeTab]);

  // Profile fetch (unchanged)
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        navigate("/login", { replace: true });
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (isMounted) {
          setUser(res.data);
          setError(null);
        }
      } catch (err) {
        try {
          const decoded = jwtDecode(token);
          if (isMounted) {
            setUser(decoded);
            setError("Using cached data");
          }
        } catch (decodeErr) {
          if (isMounted) {
            setError("Session expired");
            setTimeout(() => handleLogout(), 1500);
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProfile();
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = async () => {
    // 1) Revoke the refresh token server-side so the session actually ends.
    //    Best-effort: a failure here must never trap the user on this page.
    //    Raw axios (not `api`) so the JWT interceptor can't try to refresh the
    //    very token we are revoking.
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await axios.post(`${api.defaults.baseURL}/auth/logout`, { refreshToken });
      } catch (_err) {
        /* best effort */
      }
    }

    // 2) Clear local state UNCONDITIONALLY. This used to sit in a `finally`
    //    behind `MSAL logoutPopup()`. That call opens a popup window, so when it
    //    was blocked or hung, the await never settled, the `finally` never ran,
    //    and the button looked completely dead. Local teardown must never be
    //    gated behind it.
    localStorage.clear();
    sessionStorage.clear();

    // 3) Prefer the app-level handler (App.js) so its auth state is reset too;
    //    fall back to a direct redirect when it isn't supplied.
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }
    navigate("/login", { replace: true });

    // 4) Best-effort MSAL sign-out. Deliberately not awaited and unable to
    //    block: it opens a popup and is only meaningful for users who actually
    //    signed in through Microsoft. OTP/password users have no MSAL account,
    //    which is exactly the case that used to hang the button.
    if (instance && typeof instance.getAllAccounts === "function") {
      const accounts = instance.getAllAccounts() || [];
      if (accounts.length > 0) {
        Promise.resolve(
          instance.logoutPopup({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          })
        ).catch((err) => console.warn("MSAL logout skipped:", err?.message));
      }
    }
  };

  /* ================= STATUS FILTER LOGIC ================= */
  const filteredLogs = statusLogs.filter(log => 
    statusFilter === "all" ? true : log.status === statusFilter
  );

  // Status summary for graphics
  const statusSummary = statusLogs.reduce((acc, log) => {
    acc[log.status] = (acc[log.status] || 0) + 1;
    return acc;
  }, {});

  // Effective access gate: My Performance tab is shown only when the current
  // user's effective RBAC permission for page "my_performance" → action "view"
  // resolves to granted (employee DENY > employee ALLOW > role permission >
  // default DENY). Resolved against the /my-access snapshot (loadUserAccess),
  // never by checking the role name directly.
  const canViewPerformance = mayPerformWithRole(user?.role, "my_performance", "view", "performance.view");

  if (loading) {
    return (
      <div style={page}>
        <style>{css}</style>
        <div style={styles.centeredCard}>
          <div style={styles.loadingSpinner} />
          <h2 style={styles.loadingTitle}>Loading Profile...</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={page}>
        <style>{css}</style>
        <div style={styles.centeredCard}>
          <div style={styles.emptyIcon}></div>
          <h2 style={styles.errorTitle}>Session Expired</h2>
          <LoadingButton style={styles.buttonDanger} onClick={handleLogout}> Login</LoadingButton>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <style>{css}</style>
      <div style={styles.container}>
        <div style={styles.profileCard}>
          {/*  ENHANCED HEADER WITH GRAPHICS */}
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.title}> My Profile</h2>
              <p style={styles.subtitle}>Account & Activity Dashboard</p>
            </div>
            <span style={{
              ...styles.roleBadge,
              background: `linear-gradient(135deg, ${getStatusColor('applied')}, #1e40af)`
            }}>
               {user.role?.toUpperCase() || "HR"}
            </span>
          </div>

          {error && <div style={styles.warningBanner}>⚠️ {error}</div>}

          <div style={styles.divider} />

          {/*  ENHANCED TABS WITH ICONS & BADGES */}
          <div style={styles.tabs}>
            <button style={{...styles.tabButton, ...(activeTab === "profile" ? styles.activeTab : {})}} onClick={() => setActiveTab("profile")}>
               Profile
            </button>
            <button style={{...styles.tabButton, ...(activeTab === "resumes" ? styles.activeTab : {})}} onClick={() => setActiveTab("resumes")}>
               Resumes {resumeUpdates.length > 0 && `(${resumeUpdates.length})`}
            </button>
            <button style={{...styles.tabButton, ...(activeTab === "status" ? styles.activeTab : {})}} onClick={() => setActiveTab("status")}>
               Status {statusLogs.length > 0 && `(${statusLogs.length})`}
            </button>
            {canViewPerformance && (
              <button style={{...styles.tabButton, ...(activeTab === "my-performance" ? styles.activeTab : {})}} onClick={() => setActiveTab("my-performance")}>
                 My Performance
              </button>
            )}
          </div>

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <>
              <div style={styles.profileInfo}>
                <ProfileField label=" Full Name" value={user.name || user.username || "N/A"} />
                <ProfileField label="✉️ Email" value={user.email || "N/A"} />
                <ProfileField label=" Role" value={user.role || "HR"} />
              </div>
              <div style={styles.divider} />
              <div style={styles.logoutBox}>
                <div>
                  <strong style={styles.logoutTitle}>⚠️ Logout</strong>
                  <p style={styles.logoutSubtext}>End current session</p>
                </div>
                <LoadingButton style={styles.buttonDanger} onClick={handleLogout}> Logout</LoadingButton>
              </div>
            </>
          )}

          {/* Resumes Tab (unchanged structure) */}
          {activeTab === "resumes" && (
            
            <div style={styles.resumeSection}>
                              {/*  Resume Search */}
                <div style={{ marginBottom: "16px" }}>
                  <input
                    type="text"
                    placeholder=" Search by Candidate or Updated By"
                    value={resumeSearch}
                    onChange={(e) => setResumeSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "12px",
                      border: "2px solid #E2E8F0",
                      fontSize: "14px",
                      fontFamily: "var(--font-sans)",
                      outline: "none",
                    }}
                  />
                </div>

              {resumeLoading ? (
                <div style={styles.centeredMessage}><div style={styles.loadingSpinner} /><p>Loading...</p></div>
              ) : resumeUpdates.length === 0 ? (
                <div style={styles.centeredMessage}><div style={styles.emptyIcon}></div><p>No updates</p></div>
              ) : (
                    filteredResumes.map(item => (
    <div key={item.id} style={styles.resumeCard}>
    <div style={styles.resumeDetails}>
      <div style={styles.resumeField}>
        <strong> Candidate:</strong> {item.candidate_name}
      </div>

      {/* <div style={styles.resumeField}>
        <strong> Role:</strong> {item.candidate_role || "N/A"}
      </div> */}

      <div style={styles.resumeField}>
        <strong>‍ Updated by:</strong> {item.updated_by_name}
      </div>

      <div style={styles.resumeField}>
        <strong> Date:</strong>{" "}
        {parseDate(item.created_at).toLocaleString()}
      </div>
    </div>

    <LoadingButton style={styles.downloadBtn} onClick={() => handleDownload(item.id)}>⬇️ Download</LoadingButton>
  </div>
))

              )}
            </div>
          )}

          {/*  ENHANCED STATUS TAB WITH GRAPHICS */}
          {activeTab === "status" && (
            <div style={styles.statusSection}>
              {/*  STATUS SUMMARY GRAPHICS */}
              <div style={styles.statusSummary}>
                <h3 style={styles.sectionTitle}> Activity Overview</h3>
                <div style={styles.statusMetrics}>
                  {Object.entries(statusSummary).map(([status, count]) => (
                    <div key={status} style={{
                      ...styles.metricCard,
                      borderLeft: `4px solid ${getStatusColor(status)}`
                    }}>
                      <div style={styles.metricIcon}>{getStatusIcon(status)}</div>
                      <div>
                        <div style={styles.metricCount}>{count}</div>
                        <div style={styles.metricLabel}>{status.replace('_', ' ').toUpperCase()}</div>
                      </div>
                    </div>
                  ))}
                  {Object.keys(statusSummary).length === 0 && (
                    <div style={styles.metricEmpty}>No activity yet</div>
                  )}
                </div>
              </div>

              {/*  FILTER WITH GRAPHIC INDICATOR */}
              <div style={styles.filterSection}>
                <label style={styles.filterLabel}>Filter: </label>
                <select 
                      style={{
                        ...styles.filterSelect,
                        borderColor: getStatusColor(statusFilter),
                        background: `linear-gradient(135deg, ${getStatusColor(statusFilter)}10, transparent)`
                      }} 
                      value={statusFilter} 
                      onChange={e => setStatusFilter(e.target.value)}
                    >
                      <option value="all"> All ({statusLogs.length})</option>
                      {CANDIDATE_STATUSES.map(status => (
                        <option key={status.key} value={status.key}>{status.label}</option>
                      ))}
                    </select>

              </div>

              {/*  CARDS WITH ENHANCED GRAPHICS */}
              {statusLoading ? (
                <div style={styles.centeredMessage}>
                  <div style={styles.loadingSpinner} />
                  <p>Loading activity...</p>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div style={styles.centeredMessage}>
                  <div style={styles.emptyIcon}></div>
                  <p>No {statusFilter === "all" ? "activity" : statusFilter} found</p>
                </div>
              ) : (
                <div style={styles.statusGrid}>
                  {filteredLogs.map(item => {
                    const statusColor = getStatusColor(item.status);
                    return (
                      <div key={item.id} style={{
                        ...styles.statusCard,
                        boxShadow: `0 4px 12px ${statusColor}20`,
                        borderTop: `3px solid ${statusColor}`
                      }}>
                        <div style={styles.statusHeader}>
                          <div style={styles.candidateName}>
                             {item.first_name} {item.last_name}
                          </div>
                          <div style={{
                            ...styles.statusBadge,
                            background: `linear-gradient(135deg, ${statusColor}, ${statusColor}cc)`,
                            boxShadow: `0 2px 8px ${statusColor}40`
                          }}>
                            {getStatusIcon(item.status)} {item.status.replace('_', ' ').toUpperCase()}
                          </div>
                        </div>
                        <div style={styles.statusDate}>
                           {parseDate(item.created_at).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* My Performance Tab */}
          {canViewPerformance && activeTab === "my-performance" && (
            <div style={styles.perfSection}>
              <div style={styles.perfCard}>
                <h3 style={styles.sectionTitle}> My Performance Report</h3>
                <p style={styles.perfSubtext}>Select a date range to generate your performance report</p>

                <div style={styles.filterSection}>
                  <label style={styles.filterLabel}>Range: </label>
                  <select
                    style={{
                      ...styles.filterSelect,
                      borderColor: '#1E40AF',
                      background: 'linear-gradient(135deg, #1E40AF10, transparent)'
                    }}
                    value={perfMode}
                    onChange={e => setPerfMode(e.target.value)}
                  >
                    <option value="lastWeek"> Last Week (Mon–Sun)</option>
                    <option value="thisWeek">️ This Week (Mon–Today)</option>
                    <option value="today">⚡ Today</option>
                    <option value="custom">️ Custom Range</option>
                  </select>
                </div>

                {perfMode === "custom" && (
                  <div style={styles.perfDateRange}>
                    <div>
                      <label style={styles.filterLabel}>From: </label>
                      <input
                        type="date"
                        value={perfStartDate}
                        onChange={e => setPerfStartDate(e.target.value)}
                        style={styles.perfDateInput}
                      />
                    </div>
                    <div>
                      <label style={styles.filterLabel}>To: </label>
                      <input
                        type="date"
                        value={perfEndDate}
                        onChange={e => setPerfEndDate(e.target.value)}
                        style={styles.perfDateInput}
                      />
                    </div>
                  </div>
                )}

                <div style={styles.perfActions}>
                  <button
                    type="button"
                    style={{ ...styles.perfGenerateBtn, ...(perfGenerating ? styles.perfGenerateBtnDisabled : {}) }}
                    onClick={handleGenerateExcel}
                    disabled={perfGenerating}
                  >
                    {perfGenerating ? "⏳ Generating..." : " Generate Excel"}
                  </button>
                </div>

                {perfError && <div style={styles.perfError}>⚠️ {perfError}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ProfileField component (unchanged) */
const ProfileField = ({ label, value }) => (
  <div style={styles.field}>
    <label style={styles.fieldLabel}>{label}</label>
    <div style={styles.fieldValue}>{value || "—"}</div>
  </div>
);

/*  COMPLETE ENHANCED STYLES */
const styles = {
  container: { maxWidth: '800px', margin: '0 auto' },
  profileCard: {
    background: '#FFFFFF',
    color: '#0F172A',
    padding: '20px',
    borderRadius: '24px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    animation: 'fadeInUp 0.7s ease-out',
    fontFamily: "var(--font-sans)",
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#0F172A',
    fontFamily: "var(--font-sans)",
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748B',
    margin: '2px 0 0 0',
  },
  roleBadge: {
    color: 'white',
    padding: '8px 14px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '700',
    boxShadow: '0 6px 16px rgba(30,64,175,0.4)',
    fontFamily: "var(--font-sans)",
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  tabButton: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '2px solid #E2E8F0',
    background: '#F8FAFC',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#475569',
    fontFamily: "var(--font-sans)",
    whiteSpace: 'nowrap',
  },
  activeTab: {
    background: 'linear-gradient(135deg, #1E40AF, #1e3a8a)',
    color: 'white',
    borderColor: '#1E40AF',
    boxShadow: '0 8px 20px rgba(30,64,175,0.4)',
    transform: 'translateY(-2px)',
  },
  statusSection: { marginTop: '8px' },
  statusSummary: {
    background: 'linear-gradient(135deg, #F0F9FF, #E0F2FE)',
    padding: '16px',
    borderRadius: '16px',
    marginBottom: '12px',
    border: '1px solid #0EA5E9',
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    color: '#0F172A',
    fontFamily: "var(--font-sans)",
  },
  statusMetrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '8px',
  },
  metricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  metricIcon: {
    fontSize: '20px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.8)',
  },
  metricCount: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#0F172A',
  },
  metricLabel: {
    fontSize: '12px',
    color: '#64748B',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  filterLabel: {
    fontWeight: '600',
    color: '#0F172A',
    fontSize: '13px',
  },
  filterSelect: {
    padding: '8px 12px',
    borderRadius: '12px',
    border: '2px solid',
    background: 'white',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    minWidth: '220px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
   statusGrid: {
    display: 'grid',
    gap: '8px',
  },
  statusCard: {
    padding: '12px',
    borderRadius: '12px',
    background: 'white',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    transition: 'all 0.3s ease',
    border: '1px solid #E2E8F0',
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  candidateName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0F172A',
  },
  statusBadge: {
    color: 'white',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  statusDate: {
    fontSize: '14px',
    color: '#64748B',
    fontFamily: "var(--font-sans)",
  },
  // Include all other existing styles...
   profileInfo: { display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '16px' },
  field: { animation: 'fadeInUp 0.6s ease-out' },
  fieldLabel: { fontSize: '11px', color: '#64748B', marginBottom: '4px', display: 'block', fontWeight: '700', textTransform: 'uppercase' },
   fieldValue: { background: 'linear-gradient(135deg, #F8FAFC, #F0F4FF)', padding: '10px 14px', borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '14px', fontWeight: '600' },
   logoutBox: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#FEF2F2', borderRadius: '16px', flexWrap: 'wrap', gap: '12px' },
  logoutTitle: { fontSize: '18px', color: '#0F172A' },
  logoutSubtext: { fontSize: '14px', color: '#64748B', margin: '4px 0 0 0' },
   buttonDanger: { background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: 'white', padding: '8px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '13px', boxShadow: '0 6px 16px rgba(239,68,68,0.4)' },
  resumeSection: { display: 'flex', flexDirection: 'column', gap: '12px' },
   resumeCard: { padding: '12px', borderRadius: '16px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #F8FAFC, #F0F4FF)', flexWrap: 'wrap', gap: '8px' },
   downloadBtn: { background: 'linear-gradient(135deg, #1E40AF, #1e3a8a)', color: 'white', padding: '6px 14px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '13px', boxShadow: '0 6px 16px rgba(30,64,175,0.4)' },
  centeredCard: { maxWidth: '500px', margin: 'auto', background: 'white', padding: '32px 24px', borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', textAlign: 'center', animation: 'scaleIn 0.6s ease-out' },
  loadingSpinner: { width: '40px', height: '40px', marginBottom: '16px', border: '4px solid #E2E8F0', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingTitle: { color: '#0F172A', fontSize: '24px', margin: '0 0 12px 0' },
  errorTitle: { color: '#DC2626', fontSize: '24px', margin: '0 0 12px 0' },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  emptyText: { color: '#64748B', fontSize: '15px', margin: '12px 0 16px 0', lineHeight: '1.6' },
  centeredMessage: { textAlign: 'center', padding: '32px 16px' },
  metricEmpty: { gridColumn: '1 / -1', textAlign: 'center', color: '#94A3B8', fontSize: '14px', padding: '16px' },
  perfSection: { marginTop: '8px' },
  perfCard: {
    background: 'linear-gradient(135deg, #F0FDF4, #DCFCE7)',
    padding: '16px',
    borderRadius: '16px',
    marginBottom: '12px',
    border: '1px solid #22C55E',
  },
  perfSubtext: { fontSize: '14px', color: '#64748B', margin: '4px 0 16px 0' },
  perfDateRange: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' },
  perfDateInput: {
    padding: '8px 12px',
    borderRadius: '12px',
    border: '2px solid #E2E8F0',
    background: 'white',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  perfActions: { marginTop: '16px' },
  perfError: {
    marginTop: '12px',
    padding: '10px 14px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '12px',
    color: '#DC2626',
    fontSize: '13px',
    fontWeight: '600',
  },
  perfGenerateBtn: {
    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
    color: 'white',
    padding: '10px 18px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '13px',
    boxShadow: '0 6px 16px rgba(34,197,94,0.4)',
  },
  perfGenerateBtnDisabled: {
    opacity: '0.6',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};

const page = {
  minHeight: "auto",
  background: "transparent",
  padding: "4px 0",
  fontFamily: "var(--font-sans)",
};

const css = `
@keyframes fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@media (max-width: 768px) { .container { padding: 0 16px; } .tabs { flex-direction: column; } }
`;

